/**
 * Secure offline storage abstraction (Day 8).
 *
 * Provider-swappable encrypted key/value store for sensitive mobile data.
 *  - Now (PWA): WebCryptoProvider — AES-GCM 256, in-memory session key.
 *  - Later (native): NativeSecureProvider (Keychain/Keystore) — same interface.
 *
 * Values are stored as { v, iv, ct, exp } where ct is base64 AES-GCM ciphertext.
 * Designed so call sites never change when swapping providers for the native app.
 */

const STORAGE_PREFIX = 'gc360_sec:';

// ── Default TTLs (ms) ────────────────────────────────────────────────
export const TTL = {
  PARTICIPANT_BRIEF: 12 * 60 * 60 * 1000,
  CARE_PLAN_BRIEF: 12 * 60 * 60 * 1000,
  SHIFT_META: 24 * 60 * 60 * 1000,
  DEFAULT: 6 * 60 * 60 * 1000,
};

function b64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function unb64(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}

/** Abstract interface (documentation; JS has no interfaces). */
export class SecureStorageProvider {
  async setItem(key, value, ttlMs) { throw new Error('not implemented'); }
  async getItem(key) { throw new Error('not implemented'); }
  async removeItem(key) { throw new Error('not implemented'); }
  async clear() { throw new Error('not implemented'); }
  async keys() { throw new Error('not implemented'); }
}

/**
 * WebCryptoProvider — AES-GCM encryption with an in-memory session key.
 * The key never persists in plaintext; it is lost on tab close (ciphertext
 * then becomes unreadable and is treated as a miss — data is re-fetched online).
 */
export class WebCryptoProvider extends SecureStorageProvider {
  constructor() {
    super();
    this._key = null;
    this._available = typeof window !== 'undefined'
      && !!window.crypto?.subtle
      && !!window.localStorage;
  }

  async _getKey() {
    if (this._key) return this._key;
    // Try to restore raw key material from sessionStorage (cleared on tab close).
    const stored = this._available ? sessionStorage.getItem('gc360_sk') : null;
    if (stored) {
      this._key = await crypto.subtle.importKey(
        'raw', unb64(stored), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
      return this._key;
    }
    const key = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    const raw = await crypto.subtle.exportKey('raw', key);
    try { sessionStorage.setItem('gc360_sk', b64(raw)); } catch (_) { /* private mode */ }
    // Re-import as non-extractable for use.
    this._key = await crypto.subtle.importKey(
      'raw', raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    return this._key;
  }

  async setItem(key, value, ttlMs = TTL.DEFAULT) {
    if (!this._available) {
      // Fallback: plaintext (modern PWA targets always have WebCrypto).
      localStorage.setItem(STORAGE_PREFIX + key,
        JSON.stringify({ v: 0, plain: value, exp: Date.now() + ttlMs }));
      return;
    }
    const cryptoKey = await this._getKey();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const data = new TextEncoder().encode(JSON.stringify(value));
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, data);
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify({
      v: 1, iv: b64(iv), ct: b64(ct), exp: Date.now() + ttlMs,
    }));
  }

  async getItem(key) {
    if (!this._available) {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      if (!raw) return null;
      const rec = JSON.parse(raw);
      if (rec.exp && rec.exp < Date.now()) { await this.removeItem(key); return null; }
      return rec.plain ?? null;
    }
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    let rec;
    try { rec = JSON.parse(raw); } catch { await this.removeItem(key); return null; }
    if (rec.exp && rec.exp < Date.now()) { await this.removeItem(key); return null; }
    try {
      const cryptoKey = await this._getKey();
      const pt = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: unb64(rec.iv) }, cryptoKey, unb64(rec.ct));
      return JSON.parse(new TextDecoder().decode(pt));
    } catch {
      // Key changed/lost → unreadable; purge and miss.
      await this.removeItem(key);
      return null;
    }
  }

  async removeItem(key) {
    localStorage.removeItem(STORAGE_PREFIX + key);
  }

  async keys() {
    const out = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) out.push(k.slice(STORAGE_PREFIX.length));
    }
    return out;
  }

  async clear() {
    const keys = await this.keys();
    keys.forEach((k) => localStorage.removeItem(STORAGE_PREFIX + k));
    try { sessionStorage.removeItem('gc360_sk'); } catch (_) { /* noop */ }
    this._key = null;
  }
}

// Active provider (swap to NativeSecureProvider in the native build).
let _provider = new WebCryptoProvider();

export function setSecureProvider(provider) { _provider = provider; }
export function getSecureProvider() { return _provider; }

// Convenience facade used by call sites.
export const secureStore = {
  setItem: (k, v, ttl) => _provider.setItem(k, v, ttl),
  getItem: (k) => _provider.getItem(k),
  removeItem: (k) => _provider.removeItem(k),
  keys: () => _provider.keys(),
  clear: () => _provider.clear(),
  /** Purge expired entries proactively. */
  sweep: async () => {
    const keys = await _provider.keys();
    for (const k of keys) { await _provider.getItem(k); } // getItem purges expired
  },
};
